import { CaseData } from "@/cms/models";
import AreaDetail from "../AreaDetails";
import NumberDetail from "../NumberDetails";
import TextDetails from "../TextDetails";
import PropertyDetails from "../PropertyDetails";
import { EnergyLabelRange, formatEnergyLabelRange } from "./EnergyLabel";
import {
  getCaseClassification,
  getEstateFacilities,
  isAgricultural,
  isRental,
  shouldShowOwnerExpenses,
} from "@/utils/helper";
import BuiltYearDetails from "./propertyfacts/BuiltYearDetails";
import ExpenseDetail from "../ExpenseDetails";

interface PropertyFactProps {
  caseData: CaseData;
  showPrice: boolean;
  subdomain?: string;
  showStandardFinancing?: boolean;
}

const DefaultPropertyFacts: React.FC<PropertyFactProps> = ({
  caseData,
  showPrice,
  subdomain,
  showStandardFinancing,
}) => {
  const caseClassification = getCaseClassification(caseData);
  const isCommercialRent =
    caseData.isCommercial && isRental(caseClassification);
  const isAgri = isAgricultural(caseClassification);
  const showOwnerExpenses = shouldShowOwnerExpenses(caseData);

  const isClausBorg = subdomain === "claus-borg";

  return (
    <div className="p-4 bg-[var(--box-bg-color)] text-[var(--box-text-color)] homepage-text-sm">
      {showPrice && <TextDetails label="Pris" value={caseData.priceInfo} />}
      {showPrice && (
        <>
          {showStandardFinancing && caseData.totalMonthlyGrossPayment > 0 && (
            <ExpenseDetail
              label="Brutto (ex. ejerudgifter)"
              value={caseData.totalMonthlyGrossPayment}
            />
          )}
          {showStandardFinancing && caseData.totalMonthlyNetPayment > 0 && (
            <ExpenseDetail
              label="Netto (ex. ejerudgifter)"
              value={caseData.totalMonthlyNetPayment}
            />
          )}

          {showOwnerExpenses && (
            <ExpenseDetail
              label="Ejerudgifter pr. md."
              value={caseData.ownerExpensesTotalMonthly}
            />
          )}
        </>
      )}

      {caseData.floorArea > 0 && (
        <AreaDetail label="Etageareal" value={caseData.floorArea} />
      )}
      {!isCommercialRent && caseData.housingArea > 0 && (
        <AreaDetail label="Boligareal" value={caseData.housingArea} />
      )}
      {!isCommercialRent && caseData.commercialArea > 0 && (
        <AreaDetail label="Erhvervsareal" value={caseData.commercialArea} />
      )}
      {caseData.otherAreas > 0 && (
        <AreaDetail label="Sekundært areal" value={caseData.secondaryArea} />
      )}
      {caseData.basementArea > 0 && (
        <AreaDetail label="Kælderareal" value={caseData.basementArea} />
      )}
      {caseData.garageArea > 0 && (
        <AreaDetail label="Garage" value={caseData.garageArea} />
      )}
      {caseData.carportArea > 0 && (
        <AreaDetail label="Carport" value={caseData.carportArea} />
      )}
      {caseData.shedArea > 0 && (
        <AreaDetail label="Udhus" value={caseData.shedArea} />
      )}
      {caseData.outdoorArea > 0 && (
        <AreaDetail label="Udenomsareal" value={caseData.outdoorArea} />
      )}
      {caseData.plotArea > 0 && (
        <AreaDetail
          label="Grundareal"
          value={caseData.plotArea}
          useHectares={isAgri}
        />
      )}
      {caseData.numberOfRooms > 0 && (
        <NumberDetail label="Værelser" value={caseData.numberOfRooms} />
      )}
      {!isClausBorg && caseData.whereOfNumberOfLivingRooms > 0 && (
        <NumberDetail
          label="Heraf stuer"
          value={caseData.whereOfNumberOfLivingRooms}
        />
      )}
      {!isClausBorg && caseData.noOfStories > 0 && (
        <NumberDetail label="Antal plan" value={caseData.noOfStories} />
      )}
      {caseData.yearBuilt > 0 && (
        <BuiltYearDetails
          label="Byggeår"
          yearBuilt={caseData.yearBuilt}
          yearRenovationAddition={caseData.yearRenovationAddition}
        />
      )}
      {caseData.caseNumber && (
        <TextDetails label="Sagsnummer" value={caseData.caseNumber} />
      )}
      {caseData.estateType && (
        <TextDetails label="Ejendomstype" value={caseData.estateType} />
      )}
      {caseData.energyLabel && (
        <PropertyDetails
          label="Energimærke"
          value={formatEnergyLabelRange(
            caseData.energyLabel,
            caseData.energyLabelBest,
            caseData.energyLabelWorst,
          )}
          showUnit={false}
        >
          <EnergyLabelRange
            energyLabel={caseData.energyLabel}
            energyLabelBest={caseData.energyLabelBest}
            energyLabelWorst={caseData.energyLabelWorst}
          />
        </PropertyDetails>
      )}

      {getEstateFacilities(caseData).map((taxon) => (
        <TextDetails
          key={taxon.taxonNamespace}
          label={taxon.taxonDisplayName}
          value={taxon.note ? taxon.note.substring(0, 25) : "Ja"}
        />
      ))}
    </div>
  );
};

export default DefaultPropertyFacts;
